<script setup>
import {computed, onMounted, onUnmounted, ref} from 'vue';
import {contacts} from "@js/company_details.ts";
import {useDeliveryStore} from '@js/store/DeliveryStore.js';
import {checkDocument, formatPhone} from "@js/helpers.ts";
import {getAddressSuggestions} from "@js/yandex-api.ts";
import IconManipulatorCar1 from '@js/components/Icons/IconManipulatorCar1.vue';
import IconManipulatorCar2 from '@js/components/Icons/IconManipulatorCar2.vue';
import IconExclamationFilled from "@js/components/Icons/IconExclamationFilled.vue";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import IconMixerCar1 from "@js/components/Icons/IconMixerCar1.vue";
import IconExclamation from "@js/components/Icons/IconExclamation.vue";
import IconTruck05 from "@js/components/Icons/IconTruck05.vue";
import IconTruck15 from "@js/components/Icons/IconTruck15.vue";
import IconTruck35 from "@js/components/Icons/IconTruck35.vue";
import IconTruck5 from "@js/components/Icons/IconTruck5.vue";
import IconTruck75 from "@js/components/Icons/IconTruck75.vue";
import HorizontalDragScroll from "@js/components/HorizontalDragScroll.vue";

const formatedPhone = computed(() => formatPhone(contacts.sales_department_phone));

const delivery = useDeliveryStore();
const dropdownRef = ref(null);

// ТЕПЕРЬ ВСЕГДА манипулятор, 6м и 12м

// одна иконка на оба варианта
const carIconMap = {
    'truck_05': IconTruck05,
    'truck_15': IconTruck15,
    'truck_35': IconTruck35,
    'truck_50': IconTruck5,
    'truck_75': IconTruck75,
    'manipulator_75': IconManipulatorCar1,
    'mixer': IconMixerCar1,
};
const carIconFor = (id) => carIconMap[id] ?? IconManipulatorCar1;

function marketRound(x) {
    const ceil10 = Math.ceil(x / 10) * 10;
    return Math.max(0, ceil10 - 1);
}

function formatRUB(n) {
    return new Intl.NumberFormat('ru-RU').format(Math.round(n)) + ' ₽';
}

const vehiclesList = computed(() => delivery.vehicles);
const pricesList = computed(() => delivery.currentPrices);

let debounce = null;
const suggestions = ref([]);
const query = ref(delivery.address);

const fetchSuggestions = () => {
    clearTimeout(debounce);
    debounce = setTimeout(async () => {
        if ((query.value || '').length < 3) {
            suggestions.value = [];
            return;
        }
        suggestions.value = await getAddressSuggestions(query.value);
    }, 300);
};

const selectSuggestion = (value, lat, lon) => {
    query.value = value;
    delivery.address = value;
    suggestions.value = [];
    delivery.setCoords([lat, lon]);
};

const handleClickOutside = (event) => {
    if (!dropdownRef.value?.contains(event.target)) {
        suggestions.value = [];
    }
};

onMounted(() => {
    if (checkDocument()) {
        document.addEventListener('click', handleClickOutside);
    }
});

onUnmounted(() => {
    if (checkDocument()) {
        document.removeEventListener('click', handleClickOutside);
    }
});
</script>

<template>
    <div class="w-full">
        <h2 class="text-2xl md:text-3xl font-bold mb-2">Расчет доставки</h2>

        <!-- Адрес -->
        <div class="mb-4">
            <label class="block text-sm text-gray-600 mb-1">Адрес или координаты доставки</label>
            <div>
                <div ref="dropdownRef" class="relative mb-4">
                    <input v-model="query" @input="fetchSuggestions" type="text"
                           placeholder="Адрес доставки" autocomplete="off"
                           class="w-full mt-3 px-4 py-2 mb-1 border border-gray-300 focus:border-baseColor focus:ring-baseColor rounded-md"/>
                    <ul v-if="suggestions.length"
                        class="absolute z-10 w-full bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto">
                        <li v-for="(suggestion, i) in suggestions" :key="i"
                            @click="selectSuggestion(suggestion.label, suggestion.lat, suggestion.lon)"
                            class="px-4 py-2 md:hover:bg-gray-100 cursor-pointer text-sm">
                            {{ suggestion.label }}
                        </li>
                    </ul>
                </div>
            </div>
            <p class="text-xs text-gray-500 mt-1">
                Для расчёта стоимости доставки введите адрес текстом или выберите на карте
            </p>
        </div>

        <!-- Выбор авто (только манипулятор 6м/12м) -->
        <div class="mb-6">
            <HorizontalDragScroll :scroll-step="220">
                <div class="delivery-vehicles-list">
                    <button
                        v-for="v in vehiclesList"
                        :key="v.id"
                        type="button"
                        @click="delivery.setVehicle(v.id)"
                        class="delivery-vehicle-card"
                        :class="delivery.vehicle === v.id
                    ? 'bg-baseColor border-baseColor text-black'
                    : 'bg-gray-100 border-gray-100 text-gray-500'"
                    >
                        <component
                            :is="carIconFor(v.id)"
                            class="h-9 w-14 flex-shrink-0"
                            :class="delivery.vehicle === v.id ? 'opacity-100' : 'opacity-60'"
                        />

                        <span class="text-sm font-semibold leading-tight text-center whitespace-nowrap">
                    {{ v.label }}
                </span>
                    </button>
                </div>
            </HorizontalDragScroll>
        </div>

        <template v-if="delivery.out_of_zones">
            <div class="flex items-center gap-2 border-2 border-amber-500 p-3 rounded">
                <IconExclamationFilled class="h-4 w-4 md:h-7 md:w-7 flex-shrink-0 text-amber-500"/>
                <p class="text-[13px] md:text-sm text-gray-800 font-medium">
                    Доставка по указанному адресу возможна только по договорной цене, звоните:
                    <br class="max-md:hidden"/>
                    <a :href="'tel:+'+contacts.sales_department_phone"
                       class="font-semibold underline hover:no-underline">
                        {{ formatedPhone }}
                    </a>.
                </p>
            </div>
        </template>
        <template v-else>
            <h3 class="text-sm uppercase tracking-wide text-gray-500 mb-3">Цены на доставку</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-[15px]">
                <div
                    v-for="item in pricesList"
                    :key="item.id"
                    class="bg-gray-100 rounded-lg px-4 py-3 flex items-start justify-between gap-4"
                >
                    <div>
                        <div class="font-semibold text-gray-900">
                            {{ item.price === 1 ? '1 ₽' : 'от ' + new Intl.NumberFormat('ru-RU').format(item.price) + ' ₽' }}
                        </div>
                        <div class="text-sm text-gray-700">
                            {{ item.label }}
                        </div>
                    </div>

                    <button
                        type="button"
                        class="text-gray-500 hover:text-gray-900"
                        title="Информация"
                    >
                        <IconExclamation class="w-5 h-5"/>
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>


<style scoped>
.delivery-vehicles-list {
    display: inline-flex;
    gap: 8px;
    min-width: max-content;
}

.delivery-vehicle-card {
    min-width: max-content;
    height: 110px;
    padding: 14px 18px;
    border-radius: 10px;
    border-width: 1px;
    transition: 0.2s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-shrink: 0;
}

.delivery-vehicle-card:hover {
    filter: brightness(0.98);
}
</style>
